<template>
  <div class="manager-company-market-appraisals-ui">
    <VSpinner v-if="isLoading && !appointmentTypes.length" class="centered-block" />
    <VFallbackText v-else-if="hasError" class="centered">Something went wrong</VFallbackText>
    <VTable v-else :table="table" class="manager-market-appraisals">
      <template #col-1="{ colData }">
        <VCheckbox
          v-model="colData.isMarketAppraisal"
          @change="update(colData.id, colData.isMarketAppraisal)"
        />
      </template>
      <template #no-data-message>No data found</template>
    </VTable>
  </div>
</template>

<script setup lang="ts">
  // Types
  import type { TManagerAppointmentType } from '~/modules/manager/types';
  import type { ITable, TTableData } from '~/misc/v-table/types';

  // Models
  import { ManagerAppointmentTypesModel } from '~/modules/manager/models/appointment-types';

  // Config
  import { TABLE_HEADERS } from '~/modules/manager/company/market-appraisal/config';

  // Composables
  import { useNotifyResource } from '~/misc/resource/composables/use-notify-resource';

  // Components
  import VFallbackText from '~/misc/v-fallback-text/VFallbackText.vue';
  import VSpinner from '~/misc/v-spinner/VSpinner.vue';
  import VTable from '~/misc/v-table';
  import VCheckbox from '~/misc/v-checkbox';

  const props = defineProps({
    companyId: {
      type: Number as PropType<number | null>,
      default: null,
    },
  });

  const managerAppointmentTypesModel = new ManagerAppointmentTypesModel();

  const { notifyFailure } = useNotifyResource();

  const isLoading: Ref<boolean> = ref<boolean>(false);
  const hasError: Ref<boolean> = ref<boolean>(false);
  const appointmentTypes: Ref<TManagerAppointmentType[]> = ref<TManagerAppointmentType[]>([]);

  const tableData: ComputedRef<TTableData> = computed<TTableData>(() => {
    if (!appointmentTypes.value) return [];

    return appointmentTypes.value.map((appointmentType: TManagerAppointmentType) => [
      appointmentType.name,
      {
        id: appointmentType.id,
        isMarketAppraisal: appointmentType.is_market_appraisal,
      },
    ]);
  });

  const table: ComputedRef<ITable> = computed<ITable>(() => ({
    headers: TABLE_HEADERS,
    data: tableData.value,
  }));

  const setIsLoading = (state: boolean) => {
    isLoading.value = state;
  };

  const setHasError = (state: boolean) => {
    hasError.value = state;
  };

  const setAppointmentTypes = (data: TManagerAppointmentType[]) => {
    appointmentTypes.value = data;
  };

  const fetch = async () => {
    if (!props.companyId) return;

    try {
      setIsLoading(true);

      const response: TManagerAppointmentType[] = await managerAppointmentTypesModel.list(
        props.companyId
      );

      setAppointmentTypes(response);
    } catch {
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const update = async (typeId: number, isMarketAppraisal: boolean) => {
    try {
      setIsLoading(true);

      await managerAppointmentTypesModel.update(typeId, {
        is_market_appraisal: isMarketAppraisal,
      });
    } catch {
      notifyFailure('Something went wrong while updating appointment type');
    } finally {
      setIsLoading(false);
    }
  };

  fetch();
</script>
